import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Table, Button, Modal, Form, Input, Upload, Spin, Space, Tooltip } from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { getStaff, addStaff, updateStaff, deleteStaff } from '../../redux/staff/staffActions';
import Navbar from '../Navbar';
import './Members.css';

const Members = () => {
    const dispatch = useDispatch();
    const { staff, loading, total, page, limit } = useSelector((state) => state.staff);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form] = Form.useForm();
    const [fileList, setFileList] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const [deleteModalVisible, setDeleteModalVisible] = useState(false);
    const [deletingId, setDeletingId] = useState(null);
    const [deletingName, setDeletingName] = useState('');

    useEffect(() => {
        dispatch(getStaff());
    }, []);

    // Debug: Log staff data to check field names
    useEffect(() => {
        if (staff && staff.length > 0) {
            console.log('Staff Data:', staff);
            console.log('First staff member:', staff[0]);
            console.log('Available fields:', Object.keys(staff[0]));
        }
    }, [staff]);

    const handleAddClick = () => {
        setIsEditing(false);
        setEditingId(null);
        form.resetFields();
        setFileList([]);
        setIsModalVisible(true);
    };

    const handleEditClick = (record) => {
        setIsEditing(true);
        setEditingId(record.id);
        form.setFieldsValue({
            name: record.name,
            profession: record.profession,
        });
        const photoUrl = record.profile_photo || record.profilePhoto;
        if (photoUrl) {
            setFileList([
                {
                    uid: '-1',
                    name: 'profile_photo',
                    status: 'done',
                    url: photoUrl,
                },
            ]);
        }
        setIsModalVisible(true);
    };

    const handleCancel = () => {
        setIsModalVisible(false);
        form.resetFields();
        setFileList([]);
    };

    const handleSubmit = async (values) => {
        setSubmitting(true);
        const formData = new FormData();
        formData.append('name', values.name);
        formData.append('profession', values.profession);

        if (fileList.length > 0 && fileList[0].originFileObj) {
            formData.append('profile_photo', fileList[0].originFileObj);
        }

        if (isEditing) {
            dispatch(
                updateStaff(editingId, formData, () => {
                    setSubmitting(false);
                    handleCancel();
                })
            );
        } else {
            dispatch(
                addStaff(formData, () => {
                    setSubmitting(false);
                    handleCancel();
                })
            );
        }
    };

    const handleDelete = (id) => {
        dispatch(deleteStaff(id));
    };

    const handleDeleteClick = (record) => {
        setDeletingId(record.id);
        setDeletingName(record.name);
        setDeleteModalVisible(true);
    };

    const handleDeleteConfirm = () => {
        dispatch(
            deleteStaff(deletingId, () => {
                setDeleteModalVisible(false);
                setDeletingId(null);
                setDeletingName('');
            })
        );
    };

    const handleDeleteCancel = () => {
        setDeleteModalVisible(false);
        setDeletingId(null);
        setDeletingName('');
    };

    const handleUploadChange = ({ fileList: newFileList }) => {
        if (newFileList.length > 1) {
            setFileList([newFileList[newFileList.length - 1]]);
        } else {
            setFileList(newFileList);
        }
    };

    const columns = [
        {
            title: 'Profile Photo',
            dataIndex: 'profile_photo',
            key: 'profile_photo',
            width: 100,
            render: (text, record) => {
                // Check both profile_photo and profilePhoto field names
                const imageUrl = text || record.profilePhoto || 'https://via.placeholder.com/50?text=No+Photo';
                return (
                    <img
                        src={imageUrl}
                        alt="profile"
                        style={{ 
                            width: 50, 
                            height: 50, 
                            borderRadius: '50%', 
                            objectFit: 'cover',
                            border: '1px solid #ddd'
                        }}
                        onError={(e) => {
                            e.target.src = 'https://via.placeholder.com/50?text=No+Photo';
                        }}
                    />
                );
            },
        },
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            width: 200,
        },
        {
            title: 'Profession',
            dataIndex: 'profession',
            key: 'profession',
            width: 200,
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 150,
            render: (_, record) => (
                <Space>
                    <Tooltip title="Edit">
                        <Button
                            className="staff-btn-edit"
                            icon={<EditOutlined />}
                            onClick={() => handleEditClick(record)}
                            size="small"
                        />
                    </Tooltip>
                    <Tooltip title="Delete">
                        <Button
                            className="staff-btn-delete"
                            icon={loading && deletingId === record.id ? <Spin size="small" /> : <DeleteOutlined />}
                            onClick={() => handleDeleteClick(record)}
                            size="small"
                            disabled={loading}
                        />
                    </Tooltip>
                </Space>
            ),
        },
    ];

    return (
        <>
        <div className="container-fluid">
            <div className="row">
                <div className="col-12 px-0 bg-ff br-top-left-22 services-cont">
                    <div className="container-fluid">
                        <Navbar />
                    </div>

                    <div className="container-fluid py-4 px-md-24 px-16">
            <div className="d-flex flex-sm-row flex-column justify-content-between align-items-sm-center align-items-start gap-3 mb-4 pb-20">
                <h2 className="mb-0 f-28 fw-normal poly c-0f151d">Staff Members</h2>
                <Button
                    type="primary"
                    className="staff-add-btn"
                    icon={<PlusOutlined />}
                    onClick={handleAddClick}
                    size="large"
                >
                    Add Staff Member
                </Button>
            </div>

            <Spin spinning={loading}>
                <Table
                    columns={columns}
                    dataSource={staff}
                    rowKey="id"
                    pagination={{
                        current: page,
                        pageSize: limit,
                        total: total,
                        showSizeChanger: true,
                        pageSizeOptions: ['5', '10', '20', '50'],
                    }}
                    onChange={(pagination) => {
                        dispatch(getStaff(pagination.current, pagination.pageSize));
                    }}
                    scroll={{ x: 600 }}
                />
            </Spin>
                    </div>
                </div>
            </div>
        </div>

            <Modal
                title={isEditing ? 'Edit Staff Member' : 'Add Staff Member'}
                visible={isModalVisible}
                onCancel={handleCancel}
                footer={null}
                width={500}
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                >
                    <Form.Item
                        label="Name"
                        name="name"
                        rules={[{ required: true, message: 'Please enter staff name' }]}
                    >
                        <Input placeholder="Enter staff name" />
                    </Form.Item>

                    <Form.Item
                        label="Profession"
                        name="profession"
                        rules={[{ required: true, message: 'Please enter profession' }]}
                    >
                        <Input placeholder="Enter profession (e.g., Hairstylist, Makeup Artist)" />
                    </Form.Item>

                    <Form.Item
                        label="Profile Photo"
                    >
                        <Upload
                            listType="picture-card"
                            fileList={fileList}
                            onChange={handleUploadChange}
                            beforeUpload={() => false}
                            showUploadList={{ showPreviewIcon: true, showRemoveIcon: false }}
                        >
                            <div>
                                <PlusOutlined />
                                <div style={{ marginTop: 8 }}>
                                    {isEditing && fileList.length > 0 ? 'Change Photo' : 'Upload Photo'}
                                </div>
                            </div>
                        </Upload>
                    </Form.Item>

                    <Form.Item>
                        <Button
                            type="primary"
                            className="staff-submit-btn"
                            htmlType="submit"
                            loading={submitting}
                            block
                        >
                            {isEditing ? 'Update' : 'Add'} Staff Member
                        </Button>
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                title="Delete Staff Member"
                visible={deleteModalVisible}
                onCancel={() => !loading && handleDeleteCancel()}
                footer={[
                    <Button key="cancel" disabled={loading} onClick={handleDeleteCancel}>
                        Cancel
                    </Button>,
                    <Button
                        key="delete"
                        type="primary"
                        danger
                        disabled={loading}
                        onClick={handleDeleteConfirm}
                    >
                        {loading ? <Spin /> : 'Delete'}
                    </Button>
                ]}
                centered
            >
                <p>
                    Are you sure you want to delete <strong>{deletingName}</strong>?
                </p>
                <p style={{ color: '#ff4d4f', marginBottom: 0 }}>
                    This action cannot be undone.
                </p>
            </Modal>
        </>
    );
};

export default Members;
